import React from "react";
import {
  PdfSlateBaseParentBlockNode,
  PdfSlateEndnote,
  PdfSlateImage,
  PdfSlateLink,
  PdfSlateTextNode,
  PdfTheme,
} from "../types";
import { SlateListBlock } from "../../../../types/slate";
import Paragraph from "./Paragraph";
import { Text, View, StyleSheet } from "@surge-global-engineering/rpdf-renderer";
import { TextMessageNode, TextMessagesStyle } from "../../../Plate/plugins/text-message/types";
import { ClearFloat } from "@surge-global-engineering/react-pdf-components";

interface TextMessagesProps {
  index: number;
  nodeIndex: number;
  theme: PdfTheme;
  endNotes: PdfSlateEndnote[];
  children: (
    | PdfSlateEndnote
    | PdfSlateTextNode
    | PdfSlateLink
    | PdfSlateBaseParentBlockNode
    | SlateListBlock
    | PdfSlateImage
  )[];
  parentType: string;
  style: TextMessagesStyle;
  endNotesOffset?: number;
}

export const TextMessages: React.FC<TextMessagesProps> = ({
  index,
  nodeIndex,
  theme,
  endNotes,
  children,
  parentType,
  style,
  endNotesOffset,
}: TextMessagesProps) => {
  let sentBubbleColor = "#147efb";
  let receivedBubbleColor = "#d8d8d8";
  let recievedTextColor = "#000000";
  let bottomSideRadius = 16;

  if (style === "ios-ios") {
    sentBubbleColor = "#147efb";
  }

  if (style === "ios-android") {
    sentBubbleColor = "#53d769";
  }

  if (style === "android") {
    sentBubbleColor = "#075E54";
    receivedBubbleColor = "#444444";
    recievedTextColor = "#fff";
    bottomSideRadius = 0;
  }

  const styles = StyleSheet.create({
    container: {
      fontFamily: "SourceSansPro",
      paddingRight: 20,
      paddingLeft: 20,
    },
    contactBubble: {
      marginBottom: 1,
      fontSize: theme.properties.baseFontSize * 0.8,
      color: "#aeaeae",
    },
    sentBubble: {
      borderRadius: 16,
      borderBottomRightRadius: bottomSideRadius,
      padding: "6px 10px" ,
      marginBottom: 5,
      color: "#fff",
      backgroundColor: sentBubbleColor,
      marginLeft: 20,
    },
    receivedBubble: {
      borderRadius: 16,
      borderBottomLeftRadius: bottomSideRadius,
      padding: "6px 10px" ,
      marginBottom: 5,
      backgroundColor: receivedBubbleColor,
      color: recievedTextColor,
      marginRight: 20,
    },
  });

  const renderTextMessage = (message: TextMessageNode) => {
    if (message.messageType === "contact") {
      let contactStyle = styles.contactBubble;
      if (message.nextMessageType === "sent") {
        contactStyle = { ...contactStyle, ...{ textAlign: "right" } };
      }
      return (
        <View key={message.id} style={contactStyle} breakIfLastOnPage>
          <Text>{message.children[0].text as string}</Text>
        </View>
      );
    } else if (message.messageType === "sent") {
      return (
        <View key={message.id} style={{ display: "flex", flexDirection: "row-reverse" }} breakIfLastOnPage>
          <View style={styles.sentBubble}>
            <Paragraph
              key={`text-message-${message.id}`}
              theme={theme}
              endNotesOffset={endNotesOffset}
              nodeIndex={nodeIndex + 10}
              endNotes={endNotes}
              canJustifyText={false}
              indented={false}
              parentType={parentType}
              showFirstSentenceFormatting={false}
              style={{
                margin: 0,
                padding: 0,
              }}
            >
              {message.children}
            </Paragraph>
          </View>
        </View>
      );
    } else if (message.messageType === "received") {
      return (
        <View key={message.id} style={{ display: "flex", flexDirection: "row" }} breakIfLastOnPage>
          <View style={styles.receivedBubble}>
            <Paragraph
              key={`text-message-${message.id}`}
              theme={theme}
              endNotesOffset={endNotesOffset}
              nodeIndex={nodeIndex + 10}
              endNotes={endNotes}
              canJustifyText={false}
              indented={false}
              parentType={parentType}
              showFirstSentenceFormatting={false}
              style={{
                margin: 0,
                padding: 0,
              }}
            >
              {message.children}
            </Paragraph>
          </View>
        </View>
      );
    }
  };

  return (
    <>
    <ClearFloat />
      <View key={`text-messages-container-${index}`} style={styles.container}>
        {children.map((message) => renderTextMessage(message as TextMessageNode))}
      </View>
    <ClearFloat />
  </>
  );
};
